import { Link } from "react-router-dom";
import { loginWithGoogle } from "../lib/auth";
import { api, setStoredToken } from "../lib/api";
import { useState } from "react";
import { Button } from "../components/ui/button";
import {
  Plane, Mic, Brain, Shield, ChevronRight, Lock, BookOpen,
  GraduationCap, BarChart3, Zap, CheckCircle2, ArrowRight,
} from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/3b716827-08aa-4b01-a892-3f66dc855f4c/images/c03c1a9827339239f83d643dda1a9e6af7b6e687fee965de6028b7f44287788a.png";
const FEATURE_SIM = "https://images.pexels.com/photos/3862137/pexels-photo-3862137.jpeg";
const FEATURE_COCKPIT = "https://images.pexels.com/photos/3862634/pexels-photo-3862634.jpeg";

export default function Landing() {
  const [demoLoading, setDemoLoading] = useState(false);
  const tryDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post("/auth/demo-login");
      if (data?.session_token) setStoredToken(data.session_token);
      window.location.href = "/dashboard";
    } catch (e) {
      setDemoLoading(false);
      alert("Demo login failed. Try Google sign-in instead.");
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden border-b border-slate-200" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          {/* Full base darkening — guarantees minimum contrast across the photo */}
          <div className="absolute inset-0 bg-[var(--brand-dark)]/75" />
          {/* Left-half intensifier — solid enough to hold readable copy on any image */}
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--brand-dark)] from-0% via-[var(--brand-dark)]/85 via-50% to-[var(--brand-dark)]/40 to-100%" />
          {/* Top-to-bottom vignette */}
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--brand-dark)]/20 via-transparent to-[var(--brand-dark)]/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-28 lg:pt-32 lg:pb-36">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white/10 backdrop-blur border border-white/20 text-white/90 text-[11px] font-semibold tracking-[0.14em] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)]" />
              Aviation Training Platform
            </div>

            <h1 className="font-heading text-white mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="hero-title">
              The operating system for{" "}
              <span className="text-[var(--brand-accent)]">flight schools</span>.
            </h1>

            <p className="mt-6 text-lg text-white/90 max-w-2xl leading-relaxed">
              Aviatron turns your existing syllabus into structured digital lessons taught by AI instructors,
              automates flight debriefs from cockpit audio, and gives schools a single tenant-isolated platform
              to manage students, logbooks, and progress.
            </p>

            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-cta-row">
              <Button
                onClick={loginWithGoogle}
                className="bg-white hover:bg-slate-100 text-[var(--brand-primary)] rounded-sm px-6 py-6 text-[15px] font-semibold"
                data-testid="hero-signin-btn"
              >
                Sign in with Google <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                onClick={tryDemo}
                disabled={demoLoading}
                className="bg-[var(--brand-accent)] hover:opacity-90 text-slate-900 rounded-sm px-6 py-6 text-[15px] font-semibold"
                data-testid="hero-demo-btn"
              >
                <Zap className="w-4 h-4 mr-1.5" />
                {demoLoading ? "Loading…" : "Try the demo"}
              </Button>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  className="border-white/30 hover:border-white hover:bg-white/10 bg-transparent text-white rounded-sm px-6 py-6 text-[15px] font-semibold"
                  data-testid="hero-pricing-btn"
                >
                  View plans
                </Button>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-white/70">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--brand-accent)]" />Multi-tenant, school-grade isolation</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--brand-accent)]" />SSO via Google Workspace</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--brand-accent)]" />Stripe subscription billing</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TRUSTED STRIP ===================== */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-x-10 gap-y-2 justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
            Trusted by flight academies worldwide
          </div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm font-medium text-slate-600">
            <span className="font-heading">Maverick Aviation</span>
            <span className="text-slate-300">•</span>
            <span className="font-heading">Buzz the Tower Academy</span>
            <span className="text-slate-300">•</span>
            <span className="font-heading">Revesco Aviation</span>
            <span className="text-slate-300">•</span>
            <span className="font-heading">Iceman Flight School</span>
          </div>
        </div>
      </section>

      {/* ===================== FEATURES ===================== */}
      <section className="py-24 px-6 lg:px-10" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-primary)]">Platform capabilities</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-3">
              Everything you need to deliver structured pilot training.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              From your first PowerPoint to a published debrief in a student's logbook — Aviatron handles
              the full lifecycle of a training module, with the audit trail your regulator expects.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
            <Feature
              icon={<Brain className="w-5 h-5" />}
              title="AI Instructor Lessons"
              body="Convert your syllabus into structured modules with on-screen AI instructors, voice narration, and mid-lesson quiz checkpoints."
            />
            <Feature
              icon={<Mic className="w-5 h-5" />}
              title="Cockpit Audio Debriefs"
              body="Upload recorded flight audio. Whisper transcribes, Claude produces a structured debrief, the instructor reviews and publishes."
            />
            <Feature
              icon={<BookOpen className="w-5 h-5" />}
              title="Digital Logbook"
              body="Every flight, every recording, every published debrief — searchable, exportable, and tied to the right student."
            />
            <Feature
              icon={<BarChart3 className="w-5 h-5" />}
              title="Progress & Scoring"
              body="Per-student quiz scoring, module completion tracking, and progress reports for both instructors and check pilots."
            />
            <Feature
              icon={<Shield className="w-5 h-5" />}
              title="Tenant Isolation"
              body="Each school is a sealed tenant. Student records, recordings, and results never cross school boundaries."
            />
            <Feature
              icon={<Lock className="w-5 h-5" />}
              title="Secure Inter-School Transfer"
              body="Student moves schools? A one-time cryptographic handshake copies records to the new tenant — originals stay archived."
            />
          </div>
        </div>
      </section>

      {/* ===================== WORKFLOW ===================== */}
      <section className="py-24 px-6 lg:px-10 bg-slate-50 border-y border-slate-200" data-testid="workflow-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-primary)]">How it works</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-3">
                From cockpit to debrief in under five minutes.
              </h2>
              <div className="mt-10 space-y-7">
                <Step n="1" title="Instructor records the flight">
                  Mobile-friendly audio recorder captures the full lesson. Drop in video files up to 500 MB if you've got an in-cockpit camera.
                </Step>
                <Step n="2" title="AI transcribes & structures the debrief">
                  Whisper produces the transcript. Claude Sonnet generates a structured JSON debrief — summary, what was done well, areas to improve, and action items.
                </Step>
                <Step n="3" title="Instructor reviews & publishes">
                  Edit any field, add private CFI notes, and publish. The debrief auto-attaches to the student's logbook entry as a permanent record.
                </Step>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden border border-slate-200 shadow-lg bg-white">
                <img src={FEATURE_SIM} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-slate-200 rounded-sm shadow-lg p-5 max-w-[260px]">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Avg processing time</div>
                <div className="font-heading font-bold text-3xl text-[var(--brand-primary)] tabular-nums mt-1">3m 42s</div>
                <div className="text-xs text-slate-500 mt-1">Whisper + Claude pipeline</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== STATS ===================== */}
      <section className="py-20 px-6 lg:px-10 bg-[var(--brand-dark)] text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
          <Stat value="500MB" label="Per session upload" />
          <Stat value="< 4 min" label="AI debrief turnaround" />
          <Stat value="100%" label="Tenant isolation" />
          <Stat value="24/7" label="Student access" />
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="py-24 px-6 lg:px-10" data-testid="cta-section">
        <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-sm p-12 lg:p-16 shadow-sm relative overflow-hidden">
          <div className="relative">
            <GraduationCap className="w-9 h-9 text-[var(--brand-primary)]" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-5 max-w-2xl">
              See Aviatron with your own syllabus.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
              Try the demo academy — a complete &quot;Stalls&quot; module with AI narration, quiz checkpoints,
              and a sample debrief. Five minutes, no credit card.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={tryDemo}
                disabled={demoLoading}
                className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm px-6 py-6 text-[15px] font-semibold"
                data-testid="cta-demo-btn"
              >
                Launch demo academy <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                onClick={loginWithGoogle}
                variant="outline"
                className="border-slate-300 hover:border-[var(--brand-primary)] hover:bg-[var(--brand-light)] bg-white text-slate-900 rounded-sm px-6 py-6 text-[15px] font-semibold"
                data-testid="cta-signin-btn"
              >
                Sign in with Google
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-[var(--brand-primary)] flex items-center justify-center">
              <Plane className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-heading font-bold text-slate-900">Aviatron</span>
            <span className="text-slate-400">·</span>
            <span className="text-xs text-slate-500">Flight Training OS</span>
          </div>
          <div className="text-xs text-slate-500 tracking-wide">
            © 2026 Aviatron · Aviation training, structured.
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="bg-white p-8 hover:bg-slate-50 transition-colors" data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 rounded-sm bg-[var(--brand-light)] flex items-center justify-center text-[var(--brand-primary)]">
        {icon}
      </div>
      <h3 className="font-heading text-lg font-bold text-slate-900 mt-5 tracking-tight">{title}</h3>
      <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-5">
      <div className="flex-shrink-0 w-9 h-9 rounded-sm bg-[var(--brand-primary)] text-white flex items-center justify-center font-heading font-bold text-sm">
        {n}
      </div>
      <div>
        <div className="font-heading font-semibold text-slate-900 text-base">{title}</div>
        <div className="mt-1.5 text-slate-600 text-[14px] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading font-bold text-3xl lg:text-4xl text-white tabular-nums tracking-tight">{value}</div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.14em] text-white/60 font-semibold">{label}</div>
    </div>
  );
}
